import { WebSocketMessage, Player, PlayerStatsUpdatedPayload, PlayerReconnectedPayload } from '@/types';

export interface MessageHandlerCallbacks {
  onPlayerJoined?: (data: { roomId: string; player: Player; room: any }) => void;
  onPlayerLeft?: (data: { roomId: string; leftPlayer: Player; room: any; isTemporaryDisconnect?: boolean }) => void;
  onPlayerReady?: (data: { roomId: string; userId: string; isReady: boolean; room: any; gameStarted?: boolean; countdownCancelled?: boolean }) => void;
  onCharacterSelected?: (data: { roomId: string; userId: string; characterId: string; room: any }) => void;
  onGameStarted?: (data: { roomId: string; room: any; startingPlayerId: string; turnOrder: string[] }) => void;
  onTurnChanged?: (data: { roomId: string; room: any; previousPlayerId: string; currentPlayerId: string }) => void;
  onStatsUpdated?: (data: { roomId: string; room: any; executionResult: any; userId: string; abilityId: string }) => void;
  onPlayerEliminated?: (data: { roomId: string; playerId: string; room: any }) => void;
  onCountdownStarted?: (data: { roomId: string; remainingSeconds: number }) => void;
  onCountdownCancelled?: (data: { roomId: string }) => void;
  onPlayerStatsUpdated?: (data: PlayerStatsUpdatedPayload) => void;
  onPlayerReconnected?: (data: PlayerReconnectedPayload) => void;
}

export class MessageHandler {
  private callbacks: MessageHandlerCallbacks = {};

  constructor(callbacks: MessageHandlerCallbacks) {
    this.callbacks = callbacks;
  }

  handleMessage = (message: WebSocketMessage): void => {
    console.log('Received WebSocket message:', message);

    try {
      switch (message.type) {
        case 'player_joined':
          this.callbacks.onPlayerJoined?.(message.payload as any);
          break;

        case 'player_left': {
          // Older backend builds sent the player under `disconnectedPlayer`;
          // normalize so callbacks can always rely on `leftPlayer`.
          const payload = message.payload as any;
          this.callbacks.onPlayerLeft?.({
            ...payload,
            leftPlayer: payload.leftPlayer ?? payload.disconnectedPlayer,
          });
          break;
        }

        case 'player_ready':
          this.callbacks.onPlayerReady?.(message.payload as any);
          break;

        case 'character_selected':
          this.callbacks.onCharacterSelected?.(message.payload as any);
          break;

        case 'game_started':
          this.callbacks.onGameStarted?.(message.payload as any);
          break;

        case 'turn_changed':
          this.callbacks.onTurnChanged?.(message.payload as any);
          break;

        case 'stats_updated':
          this.callbacks.onStatsUpdated?.(message.payload as any);
          break;

        case 'player_eliminated':
          this.callbacks.onPlayerEliminated?.(message.payload as any);
          break;

        case 'countdown_started':
          this.callbacks.onCountdownStarted?.(message.payload as any);
          break;

        case 'countdown_cancelled':
          this.callbacks.onCountdownCancelled?.(message.payload as any);
          break;

        case 'player_stats_updated':
          this.callbacks.onPlayerStatsUpdated?.(message.payload as PlayerStatsUpdatedPayload);
          break;

        case 'player_reconnected':
          this.callbacks.onPlayerReconnected?.(message.payload as PlayerReconnectedPayload);
          break;

        default:
          console.warn('Unknown WebSocket message type:', message.type);
      }
    } catch (error) {
      console.error('Error handling WebSocket message:', error, message);
    }
  };

  updateCallbacks(newCallbacks: Partial<MessageHandlerCallbacks>): void {
    this.callbacks = { ...this.callbacks, ...newCallbacks };
  }
}